// Sport-pack terms: the fan-facing nouns that vary by sport.
//
// The source of truth is the backend pack (`backend/src/sports/<id>/terms.ts`),
// served on `/public/sports/:id` as `pack.terms`. This file mirrors those
// values so the label layer has the right words when a pack has not loaded
// yet, or when it comes from a localStorage cache written before `terms`
// existed. `test/site/labels/terms.sync.test.ts` keeps the two in sync.
// French and Spanish come from the sport-packs catalogue, by key (#1118).
//
// Values follow audit TERMINOLOGY.md §2c (sport-aware Game/Match) and the
// 2026-10-08 team-lead call (soccer "Full time" / "FT"). Change them here
// AND in the backend packs (and the sport-packs catalogue's en.ts).

import type { SportPack, SportTerms } from '@/contexts/SportContext'
import { sportWords } from '@/contexts/sportPackI18n'

export type { SportTerms }

export const DEFAULT_TERMS: Readonly<Record<'basketball' | 'soccer', SportTerms>> = {
  basketball: {
    game: 'Game',
    games: 'Games',
    gamesPlayedShort: 'GP',
    perGame: 'Per game',
    roundRobinRound: 'Matchday',
    onField: 'On Court',
    roster: 'Roster',
    start: 'Tip-off',
    final: 'Final',
    finalShort: 'Final',
  },
  soccer: {
    game: 'Match',
    games: 'Matches',
    gamesPlayedShort: 'MP',
    perGame: 'Per match',
    roundRobinRound: 'Matchday',
    onField: 'On Court',
    roster: 'Roster',
    start: 'Kick-off',
    final: 'Full time',
    finalShort: 'FT',
  },
}

/** The terms for a pack, in the active language: the pack's own, else this
 *  file's default for its sport, else basketball's. Missing keys are filled
 *  from the default, so a pack from an older backend never yields
 *  `undefined`. In French and Spanish the catalogue's words replace them by
 *  key; a sport the catalogue doesn't know keeps the pack's English. */
export function resolveTerms(pack: Pick<SportPack, 'id' | 'terms'> | null | undefined): SportTerms {
  const fallback =
    (pack && (DEFAULT_TERMS as Record<string, SportTerms>)[pack.id]) || DEFAULT_TERMS.basketball
  const translated = sportWords(pack?.id ?? 'basketball')?.terms
  if (!pack?.terms && !translated) return fallback
  return { ...fallback, ...pack?.terms, ...translated }
}

/** The play-area noun when no pack has loaded (mirrors the packs' venueLabel), in the active language. */
export function defaultVenueLabel(sportId: string | undefined): { singular: string; plural: string } {
  const translated = sportWords(sportId ?? 'basketball')?.venue
  if (translated) return translated
  return sportId === 'soccer' ? { singular: 'Pitch', plural: 'Pitches' } : { singular: 'Court', plural: 'Courts' }
}
